import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * AppDownloadBanner — Refined Editorial Rail
 *
 * A *truly* persistent, magazine-masthead-styled banner that nudges users on
 * the subscription web app to download the mobile app where the actual
 * product lives. Not dismissible by design — `app.germanmethod.com` has no
 * other functionality for these users, and we want the download CTA in front
 * of them on every page until they install.
 *
 * MOUNTING STRATEGY
 * -----------------
 * Uses *document-flow + sticky-top*. Rendered at the top of the App tree,
 * BEFORE the <Routes>, inside an outer `flex flex-col h-screen` wrapper. It
 * takes its natural 48px (`h-12`) of layout space and uses `sticky top-0
 * z-[60]` to stay pinned during scroll on long public pages.
 *
 * Why sticky-flow rather than `position: fixed`:
 *   - No need to add `pt-12` offsets to every public route.
 *   - Inside the protected AppLayout (`overflow-y-auto` on <main>), nothing
 *     scrolls the outer wrapper, so the banner naturally sits at the top.
 *   - Public pages with their own `min-h-screen` will scroll ~48px on short
 *     screens; sticky behavior keeps the banner visible during that scroll.
 *
 * SSR
 * ---
 * qsk-web ships SSR (see server.ts). To avoid a hydration mismatch caused by
 * the slide-in animation, the component renders `null` on the server and on
 * the first client render, then flips a `mounted` flag in a `useEffect`
 * (post-hydration) before showing.
 */

const ONELINK_URL = 'https://germanmethod.onelink.me/pW0N/xqph5jsq'

export function AppDownloadBanner() {
  const { t } = useTranslation()

  // SSR-safe: render nothing until we've hydrated, then animate in.
  const [mounted, setMounted] = useState(false)
  // Drives the slide-in / fade-in animation after mount.
  const [shown, setShown] = useState(false)

  useEffect(() => {
    setMounted(true)
    // Two-step to ensure the initial (-translate-y-full opacity-0) frame
    // commits to the DOM before transitioning to the in-view state.
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setShown(true))
    })
    return () => cancelAnimationFrame(id)
  }, [])

  if (!mounted) return null

  return (
    <div
      role="region"
      aria-label="App download promotion"
      className={[
        // Layout — document flow, sticks to viewport top during scroll.
        // `position: sticky` itself creates a containing block for the
        // absolutely-positioned gold rail below — no separate `relative` needed.
        'sticky top-0 z-[60] w-full h-12 flex-shrink-0',
        // Brand surface — vertical gradient for subtle depth, gold rules.
        'bg-gradient-to-b from-[#26247B] to-[#312D8A]',
        'border-b-[1.5px] border-[#B99F6F]/80',
        'text-white select-none',
        // Mount transition — slide + fade. On dismiss, the banner is removed
        // from the DOM after the 200ms exit completes, which collapses the
        // 48px row in the parent flex column. Acceptable: by the time content
        // beneath shifts up, the banner is already off-screen visually.
        'transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        shown ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0',
      ].join(' ')}
    >
      {/* Left edge gold rail — full-height 2px accent */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-[#B99F6F]"
      />

      <div className="container max-w-[1400px] mx-auto px-4 md:px-6 h-full flex items-center gap-2 md:gap-4">
        {/* Left cluster: monogram + headline + subtext */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          <span
            aria-hidden="true"
            className="inline-flex flex-shrink-0 items-center justify-center rounded-md border border-[#B99F6F]/40 bg-[#B99F6F]/10 text-[13px] font-semibold tracking-[0.05em] text-[#B99F6F]"
            style={{ height: '1.75rem', width: '1.75rem' }}
          >
            GM
          </span>

          {/* Desktop headline + subtext, separated by a thin rule */}
          <div className="hidden md:flex items-center gap-3 min-w-0">
            <span className="text-[14px] font-medium tracking-[-0.01em] truncate">
              {t('appBanner.headline')}
            </span>
            <span aria-hidden="true" className="h-3.5 w-px bg-white/15 flex-shrink-0" />
            <span className="text-[12px] font-normal text-white/70 truncate">
              {t('appBanner.subtext')}
            </span>
          </div>

          {/* Mobile compact headline */}
          <span className="md:hidden text-[13px] font-medium tracking-[-0.01em] truncate">
            {t('appBanner.headlineMobile')}
          </span>
        </div>

        {/* Right cluster: CTAs + dismiss */}
        <div className="flex items-center gap-2 md:gap-2.5 flex-shrink-0">
          {/* Desktop: dual app-store buttons */}
          <a
            href={ONELINK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 h-8 rounded-full px-3.5 bg-white/10 hover:bg-white/[0.18] border border-white/15 text-[13px] font-medium tracking-[0.01em] text-white transition-colors"
          >
            <svg
              aria-hidden="true"
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <span>{t('appBanner.appStore')}</span>
          </a>

          <a
            href={ONELINK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 h-8 rounded-full px-3.5 bg-white/10 hover:bg-white/[0.18] border border-white/15 text-[13px] font-medium tracking-[0.01em] text-white transition-colors"
          >
            <svg
              aria-hidden="true"
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M3.18 23.72c-.36-.17-.6-.52-.6-.92V1.2c0-.4.24-.75.6-.92L14.56 12 3.18 23.72zm1.4.56L16.25 13.4l2.67 2.67L5.8 24.28c-.43.24-.89.23-1.22 0zM16.25 10.6L4.58-.28c.33-.23.79-.24 1.22 0l13.12 8.21-2.67 2.67zm1.27 1.27l3.37-3.37c.6.34.98.95.98 1.5s-.38 1.16-.98 1.5l-3.37-3.37v3.74z" />
            </svg>
            <span>{t('appBanner.googlePlay')}</span>
          </a>

          {/* Mobile: single solid CTA */}
          <a
            href={ONELINK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="md:hidden inline-flex items-center gap-1.5 h-9 rounded-full px-4 bg-white text-[#26247B] font-medium text-[13px] hover:bg-white/95 transition-colors group"
          >
            <span>{t('appBanner.downloadCta')}</span>
          </a>
        </div>
      </div>
    </div>
  )
}
